import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadPerformance,
  clearPerformance,
} from "../../../actions/performanceAction";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import TimeRange from "@marenaud/react-timeline-range-slider";
import "swiper/swiper-bundle.css";
import moment from "moment";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import PerformanceImage from "../../performance/components/PerformanceImage";
import Export from "../../../layouts/Export";
import CircularProgress from "@mui/material/CircularProgress";
import { CardContent, Card, Tabs, Tab } from "@mui/material";
import StackedLineChartOutlinedIcon from "@mui/icons-material/StackedLineChartOutlined";
import { motion } from "framer-motion";
import EnergyReductionKPI from "./EnergyReductionKPI";
import EnergyBillSavingKPI from "./EnergyBillSavingKPI";
import HydroConsumptionKPI from "./HydroConsumptionKPI";
import GasConsumptionKPI from "./GasConsumptionKPI";
import EfficiencyRatioKPI from "./EfficiencyRatioKPI"
import BreadCrumbMUI  from '../../../layouts/BreadCrumbMUI'

// your forceUpdate hook
const useForceUpdate = () => {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update the state to force render
};

// Style for the Carousel
const cardStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "200px",
  height: "200px",
  backgroundColor: "#072545",
  border: "none",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
};

const cardContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#FFFFFF",
};

const breakpoints = {
  // when window width is >= 768px
  768: {
    slidesPerView: 3,
    spaceBetween: 20,
  },
  // when window width is >= 992px
  992: {
    slidesPerView: 4,
    spaceBetween: 30,
  },
  // when window width is >= 1200px
  1200: {
    slidesPerView: 6,
    spaceBetween: 40,
  },
  // when window width is >= 1400px
  1400: {
    slidesPerView: 7,
    spaceBetween: 50,
  },
};

const SustainabilityDetail = () => {
  let navigate = useNavigate();
  const forceUpdate = useForceUpdate();
  const dispatch = useDispatch();

  const [date, setDate] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);

  const [selectedTime, setSelectedTime] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);

  const [maxDate, setMaxDate] = useState(moment().startOf("day"));
  const [minDate, setMinDate] = useState(moment().subtract(5, "days"));

  const [error, setError] = useState(false);
  const [room, setRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedAllRooms, setSelectedAllRooms] = useState(null);
  const [modalChart, setModalChart] = useState(null);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const dark = useSelector((state) => state.dark.data);
  const performance = useSelector((state) => state.performance.data);
  const performanceLoading = useSelector((state) => state.performance.loading);
  const dashboard = useSelector((state) => state.dashboard.data);
  const building = useSelector((state) => state.building.data);
  const floor = useSelector((state) => state.floor.data);
  const userInfo = useSelector((state) => state.auth.user);
  const [filteredPerformance, setFilteredPerformance] = useState(null);

  // State of the Tabs
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // State of patten selected
  const [selectedPattern, setSelectedPattern] = useState(4);

  // State of the Swiper
  const [swiper, setSwiper] = useState(null);
  const swiperRef = useRef(null);

  // Change between group KPI tabs
  const handleTabClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
    }

    setSelectedTabIndex(index);
  };

  // Function: Format data for export feature
  const format = (report, type = 1) => {
    let arr = [];

    if (type === 1) {
      for (let i = 0; i < report?.length; i++) {
        arr.push({
          Room: report[i]?.roomId,
          Entry: report[i]?.Entry,
          Exit: report[i]?.Exit,
          Timestamp: new Date(report[i].enqueuedTime_Stamp).toISOString(),
        });
      }
    } else {
      for (let i = 0; i < report?.length; i++) {
        arr.push({
          Room: report[i]?.room?.Room,
          "Number of Persons": report[i]?.NumberOfPersons,
          "Number of Empty Seats": report[i]?.NumberOfEmptySeats,
          Timestamp: new Date(report[i].enqueuedTime_Stamp).toISOString(),
        });
      }
    }

    return arr;
  };

  // Function: Get the Avg Space Utilization
  const avgUtilization = (data, param, exact) => {
    let total = 0;
    let count = 0;
    let res = 0;

    // Check if data is available
    if (!data) {
      return "N/A";
    }
    if (data.length === 0) return "N/A";
    for (let i = 0; i < data.length; i++) {
      if (data[i][param]) {
        total += data[i][param];
        count++;
      }
    }

    if (total && count) {
      res = exact
        ? (Math.round((total / count) * 100) / 100).toFixed(2)
        : Math.round(total / count);

      // Logic: Space Utilization: No Of People * 100sqft /200 sqft - Refer the stimulation logic - GPT reference on best practice in the industry
      if (param === "NumberOfPersons") {
        res = ((res * 100) / 200) * 100;
      }
      // Logic 2: Occupancy Utilization: 1- No Of Empty Seat/12(average number of chair in each room)
      else {
        res = (1 - res / 12) * 100;
      }
    }

    return res;
  };

  // Function: Group space utilization by room for heat map
  const groupSpaceUtilization = (arr) => {
    const obj = {};
    if (arr && arr.length) {
      for (let i = 0; i < arr?.length; i++) {
        if (obj[arr[i].RoomID] === undefined) {
          obj[arr[i].RoomID] = {
            avg: arr[i].NumberOfPersons,
            count: 1,
          };
        } else {
          obj[arr[i].RoomID].avg += arr[i].NumberOfPersons;
          obj[arr[i].RoomID].count++;
        }
      }
    }
    return obj;
  };

  // Function: Group occupancy utilization by room for heat map
  const groupOccupancyUtilization = (arr) => {
    const obj = {};
    if (arr && arr.length) {
      for (let i = 0; i < arr.length; i++) {
        if (obj[arr[i].RoomID] === undefined) {
          obj[arr[i].RoomID] = {
            avg: arr[i].NumberOfEmptySeats,
            count: 1,
          };
        } else {
          obj[arr[i].RoomID].avg += arr[i].NumberOfEmptySeats;
          obj[arr[i].RoomID].count++;
        }
      }
    }

    return obj;
  };

  // Function: to get the total and round up if needed
  const totalEntryExitMetric = (data, param, exact) => {
    let total = 0;
    let count = 0;
    let res = 0;

    // Check if data is available
    if (!data) {
      return "N/A";
    }
    if (data[param].length === 0) return "N/A";
    for (let i = 0; i < data[param].length; i++) {
      if (data[param][i]) {
        total += data[param][i][param];
        count++;
      }
    }

    if (total && count) {
      res = exact
        ? (Math.round((total / count) * 100) / 100).toFixed(2)
        : Math.round(total / count);
    }

    return res;
  };

  // Function: to get the total and round up if needed by location
  const totalEntryExitMetricByLocation = (data, param, exact) => {
    let rooms;
    let resData = [];

    // Get entrance's room ID
    if (floor) {
      rooms = floor.rooms.filter((room) => {
        if (
          room.Room.includes("Entrance") ||
          room.Room.includes("Exit") ||
          room.Room.includes("Counter")
        ) {
          return room;
        }
      });
    }

    // Check if data is available
    if (!data) {
      return "N/A";
    }

    if (data[param].length === 0) return "N/A";

    // Map Room and Total Entry / Total Exit
    rooms.forEach((room) => {
      let total = 0;
      let count = 0;
      let res = 0;
      let temp = {};

      for (let i = 0; i < data[param].length; i++) {
        if (data[param][i] && room.id === data[param][i]["roomId"]) {
          total += data[param][i][param];
          count++;
        }
      }
      if (total && count) {
        res = exact
          ? (Math.round((total / count) * 100) / 100).toFixed(2)
          : Math.round(total / count);
      }

      if (res) {
        temp["roomId"] = room.id;
        temp[param] = res;
        resData.push(temp);
      }
    });

    return resData;
  };

  // Function: to group entry, exit and Occupancy by day of the week
  const EntryExitByWeekDay = (data) => {
    // Check if data is available
    if (!data) {
      return "N/A";
    }

    const dayOrder = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };

    const mergedData = [...data.Entry, ...data.Exit, ...data.Occupancy];

    const groupedByDay = mergedData.reduce((acc, data) => {
      const day = data.dayOfWeek;
      if (!acc[day]) {
        acc[day] = {
          DayOfWeek: day,
          totalEntry: 0,
          totalExit: 0,
          totalOccupancy: 0,
          count: 0,
        };
      }
      acc[day].totalEntry += data.Entry || 0;
      acc[day].totalExit += data.Exit || 0;
      acc[day].totalOccupancy += data.Occupancy || 0;
      acc[day].count += 1;

      return acc;
    }, {});

    // Format data following the positive negative bar chart
    const res = Object.values(groupedByDay).map((day) => ({
      DayOfWeek: day.DayOfWeek,
      AverageEntry: Math.round(day.totalEntry / day.count) || 0,
      AverageExit: Math.round(day.totalExit / day.count) || 0,
      AverageOccupancy: Math.round(day.totalOccupancy / day.count) || 0,
    }));

    // Sort the data by day of the week
    const sortedRes = res.sort(
      (a, b) => dayOrder[a.DayOfWeek] - dayOrder[b.DayOfWeek]
    );

    return sortedRes;
  };

  const EntryExitByHour = (data) => {
    if (!data) {
      return "N/A";
    }

    const hourOrder = {
      "09 AM": 0,
      "10 AM": 1,
      "11 AM": 2,
      "12 PM": 3,
      "01 PM": 4,
      "02 PM": 5,
      "03 PM": 6,
      "04 PM": 7,
      "05 PM": 8,
      "06 PM": 9,
    };

    const mergedData = [...data.Entry, ...data.Exit, ...data.Occupancy];

    const groupedByHour = mergedData.reduce((acc, data) => {
      const hour24 = parseInt(moment(data.DateTime).local().format("HH"), 10);
      const hour12 = moment(data.DateTime).local().format("hh A");

      if (hour24 < 9 || hour24 > 18) {
        return acc; // Skip data outside of 9 AM - 6 PM
      }

      if (!acc[hour12]) {
        acc[hour12] = {
          HourOfDay: hour12,
          totalEntry: 0,
          totalExit: 0,
          totalOccupancy: 0,
          count: 0,
        };
      }
      acc[hour12].totalEntry += data.Entry || 0;
      acc[hour12].totalExit += data.Exit || 0;
      acc[hour12].totalOccupancy += data.Occupancy || 0;
      acc[hour12].count += 1;

      return acc;
    }, {});

    // Format data following the positive negative bar chart
    const res = Object.values(groupedByHour).map((hour) => ({
      HourOfDay: hour.HourOfDay,
      AverageEntry: Math.round(hour.totalEntry / hour.count) || 0,
      AverageExit: Math.round(hour.totalExit / hour.count) || 0,
      AverageOccupancy: Math.round(hour.totalOccupancy / hour.count) || 0,
    }));

    // Sort the data by day of the week
    const sortedRes = res.sort(
      (a, b) => hourOrder[a.HourOfDay] - hourOrder[b.HourOfDay]
    );

    return sortedRes;
  };

  const EntryExitByDateHour = (data, range) => {
    let uniquePeriod_Xaxis;
    let groupedData;
    let aggregateData;
    let betweenDates = 5;

    // Check if data is available
    if (!data && data?.length) {
      return "N/A";
    }

    if (range && range?.length) {
      if (range[0].isValid && range[1].isValid) {
        betweenDates = range[1]?.diff(range[0], "days");
      }
    }

    console.log("test between days", betweenDates);

    // Selected range more than 16 days, show months
    if (betweenDates > 16) {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const hour = item.HourOfDay;
        return `${month}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, hour] = key.split("-");

        const totalEntries = entries.length;

        return {
          AggregatedPeriod_Xaxis: month,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy: Math.floor(_.meanBy(entries, "Occupancy")),
        };
      });
    }
    // show all data points
    else {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy: Math.floor(_.meanBy(entries, "Occupancy")),
        };
      });
    }
    uniquePeriod_Xaxis = [
      ...new Set(aggregateData?.map((item) => item.AggregatedPeriod_Xaxis)),
    ];

    return {
      aggregateData: aggregateData,
      uniquePeriod_Xaxis: uniquePeriod_Xaxis,
    };
  };

  // Function: to group entry, exit amd occupancy by hour by room
  const EntryExitByHourByLocation = (data) => {
    // Check if data is available
    if (!data) {
      return "N/A";
    }

    const mergedData = [...data.Entry, ...data.Exit, ...data.Occupancy];

    let groupedData = {};

    mergedData.forEach((item) => {
      const { deviceId, hourOfDay, Entry, Exit, Occupancy } = item;

      if (!groupedData[deviceId]) {
        groupedData[deviceId] = {};
      }

      if (!groupedData[deviceId][hourOfDay]) {
        groupedData[deviceId][hourOfDay] = {
          totalEntry: 0,
          totalExit: 0,
          totalOccupancy: 0,
          entryCount: 0,
          exitCount: 0,
          occupancyCount: 0,
        };
      }

      if (Entry !== undefined) {
        groupedData[deviceId][hourOfDay].totalEntry += Entry;
        groupedData[deviceId][hourOfDay].entryCount++;
      }
      if (Exit !== undefined) {
        groupedData[deviceId][hourOfDay].totalExit += Exit;
        groupedData[deviceId][hourOfDay].exitCount++;
      }
      if (Occupancy !== undefined) {
        groupedData[deviceId][hourOfDay].totalOccupancy += Occupancy;
        groupedData[deviceId][hourOfDay].occupancyCount++;
      }
    });

    let result = Object.entries(groupedData).map(([deviceId, hours]) => ({
      xAxis: "hourOfDay",
      yAxis: deviceId,
      zAxis: "averageEntryExit",
      indexVal: "indexVal",
      [deviceId]: Object.entries(hours).map(([hour, data]) => {
        // Calculate averageEntry and averageExit
        const averageEntry =
          data.entryCount > 0 ? data.totalEntry / data.entryCount : 0;
        const averageExit =
          data.exitCount > 0 ? data.totalExit / data.exitCount : 0;

        // Calculate averageEntryExit as the mean of averageEntry and averageExit
        const averageEntryExit = averageEntry + averageExit;

        return {
          hourOfDay: hour,
          indexVal: 0.5,
          averageEntry,
          averageExit,
          averageEntryExit, // Test this metric calc
          averageOccupancy:
            data.occupancyCount > 0
              ? data.totalOccupancy / data.occupancyCount
              : 0,
        };
      }),
    }));

    return result;
  };

  // Function: to convert the data to positive and negative bar chart format
  const positiveNegativeDataFormat = (data) => {
    // Check if data is available
    if (!data || data === "N/A") {
      return [];
    }

    data.map((item) => {
      item.AverageExit *= -1;
      return item;
    });

    return data;
  };

  // Aggregate data for the metrics and charts
  const aggregateData = useMemo(() => {
    return {
      TotalPeople: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Occupancy")
        : true,
      TotalEntry: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Entry")
        : true,
      TotalExit: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Exit")
        : true,
      TotalEntryByLocation: filteredPerformance
        ? totalEntryExitMetricByLocation(filteredPerformance.rawData, "Entry")
        : "",
      TotalExitByLocation: filteredPerformance
        ? totalEntryExitMetricByLocation(filteredPerformance.rawData, "Exit")
        : "",
      EntryExitByWeekDay: filteredPerformance
        ? EntryExitByWeekDay(filteredPerformance.rawData)
        : "",
      EntryExitByHour: filteredPerformance
        ? EntryExitByHour(filteredPerformance.rawData)
        : "",
      EntryExitByDateHour: filteredPerformance
        ? EntryExitByDateHour(filteredPerformance.rawData?.EntryExitRaw, [
            date[0],
            date[1],
          ])
        : "",
      EntryExitByHourByLocation: filteredPerformance
        ? EntryExitByHourByLocation(filteredPerformance.rawData)
        : "",
      AvgSpaceUtilization: filteredPerformance
        ? avgUtilization(filteredPerformance.NumberOfPersons, "NumberOfPersons")
        : "",
      AvgOccupancyUtilization: filteredPerformance
        ? avgUtilization(
            filteredPerformance.NumberOfEmptySeats,
            "NumberOfEmptySeats"
          )
        : "",
      SpaceUtilizationByLocation: filteredPerformance
        ? groupSpaceUtilization(filteredPerformance?.NumberOfPersons)
        : "",
      OccupancyUtilizationByLocation: filteredPerformance
        ? groupOccupancyUtilization(filteredPerformance?.NumberOfEmptySeats)
        : "",
    };
  }, [filteredPerformance]);

  // Effect on initial load:if user is not authenticated or building or floor not selected, navigate to dashboard page
  useEffect(() => {
    if (!dashboard || !userInfo || !floor || !building) {
      return navigate("/dashboard");
    }
  }, [floor, building, userInfo]);

  // Effect on initial load: When first loading the page, run the fetch Performance data
  useEffect(() => {
    if (performance === undefined && userInfo) {
      dispatch(
        loadPerformance(
          `between=${date[0]}--${
            date[1]
          }&params=NumberOfPersons,NumberOfEmptySeats,enqueuedTime_Stamp&user=${
            userInfo?.username
          }${building ? `&building=${building.id}` : ""}${
            floor ? `&floor=${floor.id}` : ""
          }`
        )
      );
    }
  }, [loadPerformance, userInfo]);

  // Effect on load performance and filtered performance data
  useEffect(() => {
    if (performance && !filteredPerformance) {
      setFilteredPerformance(performance);
    }
    if (!performance && filteredPerformance) {
      setFilteredPerformance(performance);
    }
  }, [performance, filteredPerformance]);

  // Effect to reset the data based on selection of the date time slider
  useEffect(() => {
    if (performance) {
      setDate([
        moment(performance.dateTimeRange.min),
        moment(performance.dateTimeRange.max),
      ]);
      setSelectedTime([
        moment(performance.dateTimeRange.min),
        moment(performance.dateTimeRange.max),
      ]);
      setMinDate(moment(performance.dateTimeRange.validMin));
      setMaxDate(moment(performance.dateTimeRange.validMax));
    }
  }, [performance, selectedAllRooms]);

  // Effect to initially set all rooms selected on heath heatmap
  useEffect(() => {
    setSelectedAllRooms("true");
  }, []);

  return (
    <div
      className="dashboard h-100 w-100 overflow-auto "
      style={{
        backgroundImage: "url('/images/building.png')"
        // backgroundColor: "#06284a",
      }}
    >
      <div className="container-fluid px-0 pb-0 pt-4  h-100 w-100">
        {/* Upper part */}
        <div className="row w-100 h-66 flex-column flex-lg-row m-0 align-items-center">
        <BreadCrumbMUI level2={'Energy Saving Measurement'} />
        <div style={{color:"#68D4FB", fontSize:"20px", width:"100%", marginX:"auto", textAlign:"center"}}>Concept Design</div>
          {/* Heatmap layout */}
          <div className="col col-lg-6 h-100 d-flex flex-column justify-content-center">
            {floor && (
              <div
                className={`text-center ${
                  dark ? "text-white" : "text-main"
                } fs-4`}
              >
                {floor.floor}
              </div>
            )}

            {/* Radio selection */}
            <div className="d-flex align-items-center justify-content-center gap-4 text-white mt-1">
              {/* Select All */}
              <div>
                <label className="d-flex justify-content-center align-items-center gap-1">
                  <input
                    type="radio"
                    value={true}
                    checked={selectedAllRooms === "true"}
                    onChange={(e) => {
                      setSelectedAllRooms(e.target.value);
                    }}
                  />
                  Select all
                </label>
              </div>

              {/* Deselect All */}
              <div>
                <label className="d-flex justify-content-center align-items-center gap-1">
                  <input
                    type="radio"
                    value={false}
                    checked={selectedAllRooms === "false"}
                    onChange={(e) => {
                      setSelectedAllRooms(e.target.value);
                    }}
                  />
                  Deselect all
                </label>
              </div>
            </div>

            <div className="mt-3 text-center mb-lg-0 mb-5 d-flex flex-column align-items-center justify-content-between">
              {floor && (
                <PerformanceImage
                  userInfo={userInfo}
                  spaceUtilization={aggregateData?.SpaceUtilizationByLocation}
                  occupancyUtilization={
                    aggregateData?.OccupancyUtilizationByLocation
                  }
                  totalEntryByLocation={aggregateData?.TotalEntryByLocation}
                  totalExitByLocation={aggregateData?.TotalExitByLocation}
                  selectedPattern={selectedPattern}
                //   selectedKPI={selectedKPI}
                  building={building}
                  floor={floor}
                  rooms={rooms}
                  loadPerformance={loadPerformance}
                  setRooms={setRooms}
                  setRoom={setRoom}
                  setSelectedAllRooms={setSelectedAllRooms}
                  selectedAllRooms={selectedAllRooms}
                  setSelectedTime={setSelectedTime}
                  date={date}
                />
              )}
            </div>
            <div
              className={`text-center ${
                dark ? "text-white" : "text-main"
              } fs-4`}
            >
              {room ? room.Room : "All rooms"}
            </div>
          </div>

          {/* Chart area */}
          <div className="col-6 h-100 d-flex flex-column  px-2 px-lg-3">
            <div className="w-100 align-items-center d-flex justify-content-end h-25 flex-column flex-lg-row ">
              <div className="h-25 d-flex align-items-center justify-content-center w-100 mb-5 mb-lg-0">
                <div className="w-100 d-flex flex-column align-items-center justify-content-center">
                  <div
                    className={`w-100 text-center ${dark ? "text-white" : ""}`}
                  >
                    <span>Selected Interval: </span>

                    <span>{moment(selectedTime[0]).format("HH:mm Do")}</span>
                    <span className="mx-2">-</span>
                    <span>{moment(selectedTime[1]).format("HH:mm Do")}</span>
                  </div>
                  <TimeRange
                    onUpdateCallback={(e) => setError(e.error)}
                    error={error}
                    ticksNumber={10}
                    className="text-white"
                    selectedInterval={selectedTime}
                    timelineInterval={date}
                    onChangeCallback={(selectedInterval) => {
                      if (filteredPerformance) {
                        const localToUTCFrom = moment(
                          selectedInterval[0]
                        ).format();
                        const localToUTCTo = moment(
                          selectedInterval[1]
                        ).format();

                        let filter = {};
                        // Filter the Utilization data
                        for (let key in performance) {
                          if (key === "rawData") {
                            filter[key] = {};
                            for (let metric in performance[key]) {
                              filter[key][metric] = [];
                              for (
                                let k = 0;
                                k < performance[key][metric].length;
                                k++
                              ) {
                                let convertTimeStamp = moment(
                                  performance[key][metric][k].DateTime
                                )
                                  .local()
                                  .format();
                                if (
                                  convertTimeStamp >= localToUTCFrom &&
                                  convertTimeStamp <= localToUTCTo
                                ) {
                                  filter[key][metric].push(
                                    performance[key][metric][k]
                                  );
                                }
                              }
                            }
                          }
                          // Filter the Utilization data
                          for (let i = 0; i < performance[key].length; i++) {
                            if (filter[key] === undefined) filter[key] = [];

                            let convertTimeStamp = moment(
                              performance[key][i].enqueuedTime_Stamp
                            )
                              .local()
                              .format();

                            if (
                              convertTimeStamp >= localToUTCFrom &&
                              convertTimeStamp <= localToUTCTo
                            ) {
                              filter[key].push(performance[key][i]);
                            }
                          }
                        }
                        setFilteredPerformance(filter);
                      }
                      setSelectedTime(selectedInterval);
                    }}
                    formatTick={(ms) => {
                      return moment(ms).format("HH:mm Do MMM");
                    }}
                  />
                </div>
              </div>

              {/* Date Picker */}
              <div className="">
                <DateRangePicker
                  clearIcon={null}
                  calendarIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      stroke={dark ? "white" : "black"}
                      stroke-width="2"
                      class="react-daterange-picker__calendar-button__icon react-daterange-picker__button__icon"
                    >
                      <rect
                        fill="none"
                        height="15"
                        width="15"
                        x="2"
                        y="2"
                      ></rect>
                      <line x1="6" x2="6" y1="0" y2="4"></line>
                      <line x1="13" x2="13" y1="0" y2="4"></line>
                    </svg>
                  }
                  onInvalidChange={() =>
                    alert(
                      "The date range picked is out of the project range. Please select the valid date range"
                    )
                  }
                  // minDate={minDate.toDate()}
                  // maxDate={maxDate.toDate()}
                  onChange={(e) => {
                    console.log(e);
                    setDate(e);
                    setSelectedTime(e);
                    dispatch(
                      loadPerformance(
                        `between=${e[0]}--${
                          e[1]
                        }&params=NumberOfPersons,NumberOfEmptySeats&user=${
                          userInfo.username
                        }${building ? `&building=${building.id}` : ""}${
                          floor ? `&floor=${floor.id}` : ""
                        }${
                          room ? `&room=${room.id}` : ""
                        }&timeRangeSelected=true`
                      )
                    );

                    setFilteredPerformance(performance);
                  }}
                  value={date}
                />
              </div>

              {/* Export the data */}
              <div className="my-4 my-lg-0">
                {filteredPerformance && (!selectedKPI || selectedKPI === 1) ? (
                  <Export
                    data={format(filteredPerformance?.rawData?.EntryExitRaw, 1)}
                    name="Entry Exit Report"
                    dark={dark}
                  />
                ) : (
                  <Export
                    data={format(filteredPerformance?.Report, 2)}
                    name="Occupancy Report"
                    dark={dark}
                  />
                )}
              </div>
            </div>

            {!selectedKPI && filteredPerformance && performance ? (
              <div className="d-flex flex-column overflow-auto align-items-center">
                <div style={{ width: "40%", height: "70%" }}>
                  <img
                    className="object-cover w-100 h-100"
                    src="/images/sustainability-screen3-3.png"
                    alt="social-employee"
                  />
                </div>

                {/* Description about*/}
                <p className="d-flex text-center text-white p-2">
                Energy Reduction measures the percentage decrease in energy use compared to a baseline period, indicating efficiency improvements.
                </p>
              </div>
            ) : selectedKPI === 1 ? (
              <>
                <div className="d-flex flex-column overflow-auto align-items-center">
                  <div style={{ width: "40%", height: "70%" }}>
                    <img
                      className="object-cover w-100 h-100"
                      src="/images/sustainability-screen3-3.png"
                      alt="social-employee"
                    />
                  </div>

                  {/* Description about*/}
                  <p className="d-flex text-center text-white p-2">
                  Energy Reduction measures the percentage decrease in energy use compared to a baseline period, indicating efficiency improvements.
                  </p>
                </div>
              </>
            ) : selectedKPI === 2 ? (
              <>
                <div className="d-flex flex-column overflow-auto align-items-center">
                <div style={{ width: "40%", height: "70%" }}>
                  <img
                    className="object-cover w-100 h-100"
                    src="/images/sustainability-screen3-3.png"
                    alt="social-employee"
                  />
                </div>
                  <p className="d-flex text-center text-white p-2">
                    Energy Bill Save tracks the reduction in energy expenses, showing the financial impact of energy-saving measures.
                  </p>
                </div>
              </>
            ) : selectedKPI === 3 ? (
              <>
                <div className="d-flex flex-column overflow-auto align-items-center">
                <div style={{ width: "40%", height: "70%" }}>
                  <img
                    className="object-cover w-100 h-100"
                    src="/images/sustainability-screen3-3.png"
                    alt="social-employee"
                  />
                </div>
                  <p className="d-flex text-center text-white p-2">
                  Hydro Consumption  tracks the total electricity usage in a building over a specific period, measured in kilowatt-hours (kWh). This metric aids in assessing energy efficiency and identifying opportunities for cost reduction.
                  </p>
                </div>
              </>
            ) : selectedKPI === 4 ? (
              <>
                <div className="d-flex flex-column overflow-auto align-items-center">
                <div style={{ width: "40%", height: "70%" }}>
                  <img
                    className="object-cover w-100 h-100"
                    src="/images/sustainability-screen3-3.png"
                    alt="social-employee"
                  />
                </div>
                  {/* Description about*/}
                  <p className="d-flex text-center text-white p-2">
                    Efficieny Ratio evaluates energy efficiency by comparing output (e.g., production, area served) to energy input, helping identify areas for improvement.
                  </p>
                </div>
              </>
            ) : selectedKPI === 5 ? (
              <>
                  <div className="d-flex flex-column overflow-auto align-items-center">
                <div style={{ width: "40%", height: "70%" }}>
                  <img
                    className="object-cover w-100 h-100"
                    src="/images/sustainability-screen3-3.png"
                    alt="social-employee"
                  />
                </div>
                  {/* Description about*/}
                  <p className="d-flex text-center text-white p-2">
                  Gas Consumption tracks total gas usage within a set period, helping monitor energy efficiency by showing trends and comparing usage to targets or previous periods.
                  </p>
                </div>
              </>
            ) : (
              performanceLoading && <CircularProgress />
            )}
          </div>
        </div>
        {/* Lower Part  */}
        <div
          className="row w-100 flex-column m-0 overflow-hidden"
          style={{ backgroundColor: "#112138" }}
        >
          <div className="col flex flex-column ">
            {/* Carousel Navigation */}
            <div className="w-100 mx-auto d-flex flex-row justify-content-center align-items-center mb-2">
              {/* Title */}
              <h5 className="text-white align-self-start pt-1">
                Monitor Board
              </h5>
              {/* Navigation Tabs */}
              <Tabs
                value={selectedTabIndex}
                onChange={(e, index) => handleTabClick(index)}
                aria-label="icon tabs example"
              >
                <Tab
                  sx={{ color: "white", fontSize: "10px" }}
                  icon={
                    <StackedLineChartOutlinedIcon sx={{ color: "white" }} />
                  }
                  aria-label="phone"
                  label="Overview"
                  iconPosition="top"
                />
              </Tabs>
            </div>
            {/* Carousel*/}

            <Swiper
              ref={swiperRef}
              modules={[Navigation, Pagination, Scrollbar, A11y]}
              spaceBetween={20}
              slidesPerView={1}
              navigation
              // pagination={{ clickable: true }}
              onSlideChange={() => setSelectedTabIndex(swiper.activeIndex)}
              onSwiper={(swiper) => setSwiper(swiper)}
            >
              {/* Overview Slide */}
              <SwiperSlide>
                <Swiper
                  centeredSlides={true}
                  centeredSlidesBounds={true}
                  centerInsufficientSlides={true}
                  breakpoints={breakpoints}
                  pagination={{ clickable: true }}
                  scrollbar={{ draggable: true }}
                  onSlideChange={() => console.log("nested slide change")}
                  onSwiper={(swiper) => console.log(swiper)}
                >
                  <SwiperSlide>
                    {/* Entry Exit */}
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(0);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <EnergyReductionKPI
                              avgUtil={97}
                              maxUtil={98}
                              minUtil={70}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>
                  <SwiperSlide>
                    {/* Space Utilization */}
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(2);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <EnergyBillSavingKPI
                              avgUtil={96}
                              maxUtil={98}
                              minUtil={70}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>
                  <SwiperSlide>
                    {/* Occupancy Utilization */}
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(3);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <HydroConsumptionKPI
                              avgUtil={98}
                              maxUtil={98}
                              minUtil={70}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>
                  <SwiperSlide>
                    {/* Occupancy Utilization */}
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(5);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <GasConsumptionKPI
                              avgUtil={98}
                              maxUtil={98}
                              minUtil={70}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>
                  {/* Hide the Guest Vs People not super admin */}
                  {userInfo?.role_id === 1 && (
                    <SwiperSlide>
                      {/* Guest vs Employee*/}
                      {filteredPerformance && performance ? (
                        <motion.div
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#0b4569",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedKPI(4)
                          }}
                          className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                        >
                          <Card sx={cardStyle}>
                            <CardContent sx={cardContentStyle}>
                              <EfficiencyRatioKPI
                                avgUtil={0.8}
                                maxUtil={2.0}
                                minUtil={4.0}
                              />
                            </CardContent>
                          </Card>
                        </motion.div>
                      ) : (
                        <Card sx={cardStyle}>
                          <CardContent>
                            {performanceLoading && <CircularProgress />}
                          </CardContent>
                        </Card>
                      )}
                    </SwiperSlide>
                  )}
                </Swiper>
              </SwiperSlide>
            
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SustainabilityDetail;
